import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import {
  DataGridFlexSales,
  DataGridFlexSalesHeader,
  useDataGridFlexSales,
  useDataGridSync,
} from '../../DataGridFlexSales';

import { reportCausesFilterCauseSelector, reportClosingSalesCausesSelector } from '../../../state';
import handlerFactory, { SalesMissingsDataGridContext } from './handlerFactory';
import {
  ValidationErrorCause,
  DifferencesCause,
  MissingCauses,
  Translation,
  RemoteResource,
  MissingClosingSalesCauses,
} from '../../../types';
import { ValidationErrorsList } from '../ValidationErrorsList';

const Container = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isDifferenceError',
})<{ isDifferenceError: boolean }>(({ isDifferenceError }) => ({
  width: '100%',
  ...(isDifferenceError
    ? {
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& [data-tour="report-results"]': {
          height: '100%',
          minHeight: 0,
        },
      }
    : {
        height: 180,
      }),
}));

interface DataGridConfigProps {
  t: Translation;
  resource: RemoteResource<MissingClosingSalesCauses | undefined>;
}

const DataGridConfig = ({ t, resource }: DataGridConfigProps) => {
  const { handler } = useDataGridFlexSales<
    DifferencesCause | ValidationErrorCause,
    SalesMissingsDataGridContext
  >();

  const { loading } = resource;
  const data = resource.data?.details || [];

  const gridContext = useMemo(
    () => ({
      t,
    }),
    [t]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    loading,
  });

  return null;
};

const SaleErrors = () => {
  const { t } = useTranslation();
  const salesResource = useSelector(reportClosingSalesCausesSelector);
  const cause = useSelector(reportCausesFilterCauseSelector);
  const isDifferenceError = cause === MissingCauses.DIFFERENCE_BETWEEN_CLOSING_AND_DETAIL;
  return (
    <Container isDifferenceError={isDifferenceError}>
      {isDifferenceError ? (
        <>
          <DataGridFlexSalesHeader>
            {t('missing-closing-causes.columns.sales-details')}
          </DataGridFlexSalesHeader>
          <DataGridFlexSales handlerFactory={handlerFactory} hideToolbar>
            <DataGridConfig t={t} resource={salesResource} />
          </DataGridFlexSales>
        </>
      ) : (
        <ValidationErrorsList
          header={t('missing-closing-causes.columns.sales-details')}
          resource={salesResource}
        />
      )}
    </Container>
  );
};

export default SaleErrors;
